import React, { useState, useEffect, useRef } from "react";
import { loadStripe, Appearance } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { auth } from "../../firebase/firebase-config";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import LoadingScreen from "../../screens/LoadingScreen";
import PaymentForm from "./PaymentForm";
import "react-toastify/dist/ReactToastify.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY!);

const appearance: Appearance = { theme: "stripe" };

interface PaymentProps {
    advanceStep: () => void;
    goBackStep: () => void;
    role: string | null;
    isOnboarding: boolean;
    refresh?: boolean;
}

const Payment: React.FC<PaymentProps> = ({
    advanceStep,
    goBackStep,
    role,
    isOnboarding,
    refresh = false,
}) => {
    const [clientSecret, setClientSecret] = useState<string>("");
    const [subscriptionId, setSubscriptionId] = useState<string>("");
    const [purchaseDetails, setPurchaseDetails] = useState<{
        name: string;
        price: number;
        amountDue: number;
        nextDate: number;
        discount: number;
        promoDuration?: {
            type: string;
            months: number | null;
        };
        isMinimumChargeApplied?: boolean;
    } | null>(null);

    const [promoCode, setPromoCode] = useState<string>("");
    const [promoStatus, setPromoStatus] = useState<-1 | 0 | 1>(0); // -1=invalid 0=none 1=applied
    const [loading, setLoading] = useState<boolean>(true);
    const hasFetched = useRef(false);

    useEffect(() => {
        if (role === "TEAM_USER") {
            goBackStep();
            return;
        }
        if (hasFetched.current) return;
        hasFetched.current = true;

        (async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const token = await auth.currentUser?.getIdToken(true);

                const { data } = await axios.get(
                    `${backendUrl}/onboarding/create-stripe-account`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (data.status?.toLowerCase?.() === "active") {
                    window.location.reload();
                    return;
                }

                if (!data.clientSecret || !data.subscriptionId) {
                    throw new Error("Stripe response missing fields");
                }

                setClientSecret(data.clientSecret);
                setSubscriptionId(data.subscriptionId);
                setPurchaseDetails(data.purchaseDetails);
            } catch (err) {
                console.error(err);
                toast.error(
                    <CustomToast
                        message="Error creating payment intent. Please try again."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                goBackStep();
            } finally {
                setLoading(false);
            }
        })();
    }, [role, goBackStep]);

    const applyPromo = async () => {
        if (!promoCode.trim()) return;
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const token = await auth.currentUser?.getIdToken(true);

            const { data } = await axios.post(
                `${backendUrl}/onboarding/apply-promo`,
                {
                    subscriptionId,
                    promoCode: promoCode.trim(),
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setClientSecret(data.clientSecret);
            setPurchaseDetails(data.purchaseDetails);
            setSubscriptionId(data.subscriptionId);
            setPromoStatus(1);
        } catch (err: any) {
            console.error(err);
            setPromoStatus(-1);
        } finally {
            setLoading(false);
        }
    };

    const removePromo = async () => {
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const token = await auth.currentUser?.getIdToken(true);

            const { data } = await axios.delete(
                `${backendUrl}/onboarding/promo/${subscriptionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setClientSecret(data.clientSecret);
            setPurchaseDetails(data.purchaseDetails);
            setSubscriptionId(data.subscriptionId);
            setPromoStatus(0);
        } catch (err) {
            console.error(err);
            toast.error(
                <CustomToast message="Could not remove promo." type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
            setPromoCode("");
        }
    };

    if (loading) return <LoadingScreen />;
    if (!clientSecret || !purchaseDetails) return null;

    return (
        <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{ clientSecret, appearance }}
        >
            <PaymentForm
                details={purchaseDetails}
                isOnboarding={isOnboarding}
                advanceStep={advanceStep}
                goBackStep={goBackStep}
                refresh={refresh}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                applyPromo={applyPromo}
                removePromo={removePromo}
                promoStatus={promoStatus}
                subscriptionId={subscriptionId}
            />
        </Elements>
    );
};

export default Payment;
